/* Copilot questions over the real API. No X-Lumiq-Tenant header — hard rule 1.
 *
 * **Every ask mints a fresh `Idempotency-Key`, deliberately breaking this codebase's usual rule.**
 * `idempotencyKeyFor` exists so a retry of one decision reuses its key; asking the copilot the same
 * question twice is *two questions*, not a retry — the metadata may have changed between them, and
 * a stable key would make the second ask replay the first answer. The same reasoning
 * `forgetIdempotencyKey` encodes for a form that registers two connections.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { isAnswerFields, isCopilotCapabilities } from '@/shared/api/generated/ai-copilot';
import type {
  AnswerFields,
  CopilotCapabilities,
} from '@/shared/api/generated/ai-copilot';
import { getOne, post } from '@/shared/api/http';

export const QUESTIONS_URL = '/v1/ai/copilot/questions';
export const CAPABILITIES_URL = '/v1/ai/copilot/capabilities';

/** The ask response: the answer fields plus what the model was shown.
 *
 * Declared here rather than generated, because `AskedResponse` is an `allOf` the emitter does not
 * handle — the same shape `ScenarioDetail` takes in the simulation slice.
 */
export interface Asked extends AnswerFields {
  readonly authorized_kinds: readonly string[];
  readonly snippet_count: number;
  readonly context_preview: string;
}

export interface StoredAnswer extends AnswerFields {
  readonly id: string;
  readonly question: string;
  /** When it was asked, as an instant.
   *
   * **Stored since migration 0059 and only now on the wire.** `recent` has always ordered by it,
   * so a client could sort the history and had nothing to label it with — which is the one gap the
   * redesigned screen needed closing, because its history list shows when each question was asked.
   *
   * An instant rather than a formatted string: the server does not know the reader's locale.
   */
  readonly asked_at: string;
}

function isAsked(value: unknown): value is Asked {
  if (!isAnswerFields(value)) return false;
  const candidate = value as unknown as Record<string, unknown>;
  return (
    Array.isArray(candidate['authorized_kinds']) &&
    typeof candidate['snippet_count'] === 'number' &&
    typeof candidate['context_preview'] === 'string'
  );
}

function isStoredAnswer(value: unknown): value is StoredAnswer {
  if (!isAnswerFields(value)) return false;
  const candidate = value as unknown as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['question'] === 'string' &&
    typeof candidate['asked_at'] === 'string'
  );
}

export function useCapabilities(): UseQueryResult<CopilotCapabilities> {
  return useQuery({
    queryKey: ['copilot', 'capabilities'],
    queryFn: () =>
      getOne(CAPABILITIES_URL, isCopilotCapabilities, 'copilot capabilities'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTranscript(): UseQueryResult<readonly StoredAnswer[]> {
  return useQuery({
    queryKey: ['copilot', 'transcript'],
    queryFn: async () => {
      const body = await getOne(
        QUESTIONS_URL,
        (value): value is { readonly answers: unknown } =>
          typeof value === 'object' && value !== null && 'answers' in value,
        'copilot transcript',
      );
      const answers: unknown = body.answers;
      if (!Array.isArray(answers)) throw new Error('answers is not an array');
      return answers.filter(isStoredAnswer);
    },
  });
}

export function useAsk(): UseMutationResult<Asked, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (question: string) =>
      /* A fresh key per ask — see the module docstring. */
      post(QUESTIONS_URL, `ask-${crypto.randomUUID()}`, isAsked, 'copilot answer', {
        question,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['copilot', 'transcript'] });
    },
  });
}
